/**
 * The route table. This is the single source of truth for:
 *   - what React Router renders
 *   - the <title>/<meta> each page gets (at runtime AND in the static HTML
 *     that scripts/generate-seo.js writes at build time)
 *   - which URLs end up in sitemap.xml
 *
 * Adding a page here is the only step required for all three to stay in sync.
 * CommonJS so the Node build script can require it.
 */
const site = require('./site');
const services = require('./services');
const industries = require('./industries');
const articles = require('./articles');
const work = require('./work');

const staticRoutes = [
  {
    path: '/',
    name: 'Home',
    nav: true,
    priority: 1.0,
    changefreq: 'weekly',
    title: 'Codelithic Labs | Software Development & IT Consulting in Calgary',
    description: site.description,
  },
  {
    path: '/about',
    name: 'About',
    nav: true,
    priority: 0.8,
    changefreq: 'monthly',
    title: 'About Codelithic Labs | Calgary Technology Partner',
    description:
      'Meet Codelithic Labs, a Calgary-based technology team building software, cloud and security solutions for clients worldwide. Our story and how we work.',
  },
  {
    path: '/services',
    name: 'Services',
    nav: true,
    priority: 0.9,
    changefreq: 'monthly',
    title: 'Software, Cloud & Security Services | Codelithic Labs',
    description:
      'Web and mobile development, UI/UX design, cloud, custom software, consulting, cybersecurity and digital marketing. Calgary-based, working worldwide.',
  },
  {
    path: '/process',
    name: 'Our Process',
    nav: true,
    priority: 0.7,
    changefreq: 'yearly',
    title: 'How We Work | Codelithic Labs',
    description:
      'Discover, design, build, launch. How Codelithic Labs runs a project, what you can expect at each stage, and what we need from you to keep it moving.',
  },
  {
    path: '/contact',
    name: 'Contact',
    nav: true,
    priority: 0.8,
    changefreq: 'yearly',
    title: 'Contact Codelithic Labs | Calgary, Alberta',
    description:
      'Start a conversation with Codelithic Labs. Based in Calgary, working with clients worldwide. Tell us about your project and we reply within a day.',
  },
  {
    path: '/industries',
    name: 'Industries',
    nav: true,
    priority: 0.8,
    changefreq: 'monthly',
    title: 'Who We Work With | Codelithic Labs',
    description:
      'Technology for startups and for energy, professional services, logistics, healthcare, construction and retail teams worldwide. See how we work in yours.',
  },
  {
    path: '/work',
    name: 'Work',
    nav: true,
    priority: 0.9,
    changefreq: 'monthly',
    title: 'Our Work | Codelithic Labs',
    description:
      'Selected projects: a community meat-share platform, a verified property marketplace and a jewellery storefront. What we built and how it works.',
  },
  {
    path: '/insights',
    name: 'Insights',
    nav: true,
    priority: 0.8,
    changefreq: 'weekly',
    title: 'Insights | Codelithic Labs',
    description:
      'Practical writing on software, cloud, security and search for business owners: what things cost, what works, and what to ignore.',
  },
  {
    path: '/privacy-policy',
    name: 'Privacy Policy',
    nav: false,
    priority: 0.3,
    changefreq: 'yearly',
    title: 'Privacy Policy | Codelithic Labs',
    description: 'How Codelithic Labs Inc. collects, uses and protects personal information.',
  },
  {
    path: '/terms-of-service',
    name: 'Terms of Service',
    nav: false,
    priority: 0.3,
    changefreq: 'yearly',
    title: 'Terms of Service | Codelithic Labs',
    description: 'The terms governing use of the Codelithic Labs Inc. website and services.',
  },
];

// One route per service, derived rather than hand-maintained.
const serviceRoutes = services.map((s) => ({
  path: `/services/${s.slug}`,
  name: s.name,
  nav: false,
  priority: 0.8,
  changefreq: 'monthly',
  title: s.metaTitle,
  description: s.metaDescription,
  service: s.slug,
}));

const industryRoutes = industries.map((i) => ({
  path: `/industries/${i.slug}`,
  name: i.name,
  nav: false,
  priority: 0.7,
  changefreq: 'monthly',
  title: i.metaTitle,
  description: i.metaDescription,
  industry: i.slug,
}));

const workRoutes = work.map((w) => ({
  path: `/work/${w.slug}`,
  name: w.name,
  nav: false,
  priority: 0.8,
  changefreq: 'yearly',
  title: w.metaTitle,
  description: w.metaDescription,
  work: w.slug,
}));

const articleRoutes = articles.map((a) => ({
  path: `/insights/${a.slug}`,
  name: a.title,
  nav: false,
  priority: 0.6,
  changefreq: 'yearly',
  title: a.metaTitle,
  description: a.metaDescription,
  article: a.slug,
  // Real publication dates give the sitemap a meaningful lastmod.
  lastmod: a.updated || a.published,
}));

/** Filename that scripts/generate-og.js writes for a route. */
const ogFor = (p) => `/og/${p === '/' ? 'home' : p.slice(1).replace(/\//g, '-')}.jpg`;

const routes = [...staticRoutes, ...serviceRoutes, ...industryRoutes, ...workRoutes, ...articleRoutes];

/*
 * Assigned in place rather than with .map(). Webpack statically analyses
 * `module.exports = {...}` on a CommonJS module to work out its named
 * exports, and building `routes` from a chained .map() defeats that analysis
 * — every named import from this file then fails with
 * "'navRoutes' is not exported". Keep this as a mutation.
 */
routes.forEach((r) => {
  r.ogImage = ogFor(r.path);
});

module.exports = {
  routes,
  staticRoutes,
  serviceRoutes,
  industryRoutes,
  workRoutes,
  articleRoutes,
  navRoutes: staticRoutes.filter((r) => r.nav),
  byPath: (p) => routes.find((r) => r.path === p),
};
